import { View } from "ol";
import Draw from "ol/interaction/Draw";
import TileLayer from "ol/layer/WebGLTile";
import VectorSource from "ol/source/Vector";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";

import { MapContext } from "../../../providers/mapProvider.js";
import "../ol.css";

import Collection from "ol/Collection";
import { useSelector } from "react-redux";
import { RecordType } from "../../../api/consts.js";
import useViewerDisplayMode from "../../../hooks/useViewerDisplayMode.js";
import { DisplayType } from "../../../redux/viewer/viewerReducer.js";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../../consts/i18n.js";
import { useProjection } from "../../../hooks/useProjection.js";
import GeoTIFFSource from "ol/source/GeoTIFF.js";
import {
  addChangeListenerToFeature,
  addNewMeasureFeatures,
  addNewRecordFeatures,
  assignNameToFeature,
  assignUniqueIdToFeature,
  createAndConfigureMap,
  extendResolutions,
  markFeatureForModificationIfNeeded,
  removeMeasureFeatures,
  removeRecordFeatures,
  setHiddenForMeasures,
  setHiddenForRecords,
  setHideNonSolarPanels,
} from "./tiffHelpers.ts";
import {
  getDrawType,
  handleDrawStart,
  triggerFinishDrawing,
  preventContextMenu,
  ToolMode,
} from "./tiffDrawHelpers.ts";
import { featureStyleFunction } from "./TiffStyles.ts";

const TiffViewer = ({
  url,
  toolMode,
  onAddFeature,
  onChangeFeature,
  onAbortFeature,
  records,
  measures,
  nextFeatureName,
}) => {
  const [displayMode] = useViewerDisplayMode();
  const { projection } = useProjection();
  const [tooltip, setTooltip] = useState(null); // State for tooltip
  const abortRef = useRef(false);

  const {
    hidden: hiddenRecords,
    selected: selectedRecords,
    editable: editableRecords,
    changes: changesRecords,
  } = useSelector((state) => state.records);
  const {
    hidden: hiddenMeasures,
    selected: selectedMeasures,
    editable: editableMeasures,
  } = useSelector((state) => state.measures2);

  const mapRef = useRef();
  const dispatch = useDispatch();

  const { map, setMap } = useContext(MapContext);
  const vectorSourceRef = useRef(new VectorSource());

  const modifyFeatures = useRef(new Collection());

  const { t } = useTranslation(namespaces.viewer);

  // hide, select record features
  useEffect(() => {
    if (!map) return;
    if (!hiddenRecords) return;
    if (!selectedRecords) return;

    const vectorSource = vectorSourceRef.current;

    const features = vectorSource.getFeatures();

    features.forEach((feature) => {
      const id = feature.getId();
      if (!id) return;
      const isRecord = feature.get("isRecord") === true;
      if (!isRecord) return;

      const isHidden = hiddenRecords.includes(id);
      feature.set("hidden", isHidden);
      const isSelected = selectedRecords.includes(id);
      feature.set("selected", isSelected);
    });
  }, [map, hiddenRecords, selectedRecords]);

  // hide select measure features
  useEffect(() => {
    if (!map) return;
    if (!hiddenMeasures) return;
    if (!selectedMeasures) return;
    const vectorSource = vectorSourceRef.current;

    const features = vectorSource.getFeatures();

    features.forEach((feature) => {
      const id = feature.getId();
      if (!id) return;

      const isRecord = feature.get("isRecord") === true;
      if (isRecord) return;

      const isHidden = hiddenMeasures.includes(id);
      feature.set("hidden", isHidden);
      const isSelected = selectedMeasures.includes(id);
      feature.set("selected", isSelected);
    });
  }, [map, selectedMeasures, hiddenMeasures]);

  const handleAddFeature = useCallback(
    (e) => {
      const feature = e.feature;

      if (feature.get("doNotAdd")) {
        // remove feature from source
        const vectorSource = vectorSourceRef.current;
        vectorSource.removeFeature(feature);
        onAbortFeature();
        return;
      }

      if (
        feature.get("isRecord") ||
        feature.get("isMeasure") ||
        feature.get("isSolarPanel")
      ) {
        // already created
        return;
      }

      // if in display mode records, then set isRecord to true, but not for measures
      if (
        displayMode === DisplayType.RECORDS &&
        feature.get("isMeasure") !== true
      ) {
        feature.set("isRecord", true);
      }

      if (displayMode === DisplayType.SOLAR_PANELS) {
        feature.set("isSolarPanel", true);
      }

      assignUniqueIdToFeature(feature);
      assignNameToFeature(feature, nextFeatureName);
      onAddFeature(feature);
      addChangeListenerToFeature(feature, onChangeFeature);
      markFeatureForModificationIfNeeded(feature, modifyFeatures);
    },
    [
      displayMode,
      nextFeatureName,
      onAddFeature,
      onChangeFeature,
      onAbortFeature,
    ],
  );

  useEffect(() => {
    const vectorSource = vectorSourceRef.current;
    const addFeatureHandler = handleAddFeature;
    vectorSource.on("addfeature", addFeatureHandler);
    return () => {
      vectorSource.un("addfeature", addFeatureHandler);
    };
  }, [handleAddFeature]);

  useEffect(() => {
    if (!map || !records) return;

    const vectorSource = vectorSourceRef.current;
    const features = vectorSource.getFeatures();

    removeRecordFeatures(vectorSource, features);

    // apply changes to records
    let recent;
    recent = records
      .map((record) => {
        return {
          ...record,
          ...changesRecords[record.id],
        };
      })
      .filter((r) => r.type !== RecordType.SOLAR_PANEL);

    addNewRecordFeatures(vectorSource, recent);

    vectorSource.getFeatures().forEach((feature) => {
      addChangeListenerToFeature(feature, onChangeFeature);
    });
  }, [changesRecords, displayMode, map, onChangeFeature, records]);

  useEffect(() => {
    if (!map || !measures) return;

    const vectorSource = vectorSourceRef.current;
    const features = vectorSource.getFeatures();

    removeMeasureFeatures(vectorSource, features);
    addNewMeasureFeatures(vectorSource, measures);

    vectorSource.getFeatures().forEach((feature) => {
      addChangeListenerToFeature(feature, onChangeFeature);
    });
  }, [map, measures, onChangeFeature]);

  useEffect(() => {
    if (!map) return;

    const vectorSource = vectorSourceRef.current;
    const features = vectorSource.getFeatures();

    switch (displayMode) {
      case DisplayType.MEASURES:
        setHiddenForRecords(features, measures);
        break;
      case DisplayType.RECORDS:
        setHiddenForMeasures(features);
        break;
      case DisplayType.SOLAR_PANELS:
        setHideNonSolarPanels(features);
        break;
      default:
        break;
    }

    // refresh to update styles
    vectorSource.changed();
  }, [displayMode, map, records, measures]);

  // add editable measures and editable records to modifyFeatures
  useEffect(() => {
    if (!map) return;
    if (!modifyFeatures) return;
    const _modifyFeatures = modifyFeatures.current;

    const vectorSource = vectorSourceRef.current;
    const features = vectorSource.getFeatures();

    const addIdsToModifyFeatures = (ids) => {
      ids.forEach((id) => {
        const feature = features.find((f) => f.getId() === id);

        if (feature) {
          feature.set("editable", true);
          _modifyFeatures.push(feature);
        }
      });
    };

    if (editableMeasures) addIdsToModifyFeatures(editableMeasures);
    if (editableRecords) addIdsToModifyFeatures(editableRecords);

    return () => {
      _modifyFeatures.clear();
    };
  }, [map, measures, records, editableMeasures, editableRecords]);

  useEffect(() => {
    if (!url) {
      return;
    }

    const tifSource = new GeoTIFFSource({
      sources: [{ url }],
    });

    let isCleanedUp = false;
    let map = null;
    let modifyInteraction = null;

    tifSource.getView().then((viewOptions) => {
      const extendedResolutions = extendResolutions(viewOptions);

      // increase active area
      const increaseExtent = (extent, padding) => {
        const [minX, minY, maxX, maxY] = extent;
        const width = maxX - minX;
        const height = maxY - minY;
        const newMinX = minX - width * padding;
        const newMinY = minY - height * padding;
        const newMaxX = maxX + width * padding;
        const newMaxY = maxY + height * padding;
        return [newMinX, newMinY, newMaxX, newMaxY];
      };

      const mapView = new View({
        ...viewOptions,
        resolutions: extendedResolutions,
        extent: increaseExtent(viewOptions.extent, 0.25),
        projection,
      });

      tifSource.on("change", () => {
        if (tifSource.getState() === "error") {
          console.error(tifSource.getError());
        }
      });

      const vectorSource = vectorSourceRef.current;

      if (!isCleanedUp) {
        const { map: createdMap, modifyInteraction: createdInteraction } =
          createAndConfigureMap(
            mapView,
            tifSource,
            vectorSource,
            modifyFeatures,
            setTooltip,
            t,
          );
        map = createdMap;
        modifyInteraction = createdInteraction;

        map.setTarget(mapRef.current);
        setMap(map);
      }
    });

    return () => {
      isCleanedUp = true;
      if (map) {
        map.setTarget(undefined);
        map.removeInteraction(modifyInteraction);
      }
    };
  }, [url, dispatch, setMap, projection, t]);

  useEffect(() => {
    if (map === null) {
      return;
    }

    if (url == null || url === "") {
      return;
    }

    let isSubscribed = true;
    const tifSource = new GeoTIFFSource({
      sources: [{ url }],
    });

    tifSource.on("change", () => {
      if (isSubscribed && tifSource.getState() === "error") {
        console.error(tifSource.getError());

        // try to reset source
        map.getLayers().forEach((layer) => {
          if (layer instanceof TileLayer) {
            layer.setSource(tifSource);
          }
        });
      }
    });

    if (map) {
      map.getLayers().forEach((layer) => {
        if (layer instanceof TileLayer) {
          layer.setSource(tifSource);
        }
      });
    }

    return () => {
      isSubscribed = false;
    };
  }, [url, map]);

  // draw interaction
  useEffect(() => {
    if (!map || toolMode === ToolMode.NONE) return;

    const vectorSource = map.getLayers().getArray()[1].getSource();

    const drawInteraction = new Draw({
      source: vectorSource,
      type: getDrawType(toolMode),
      style: (feature) =>
        featureStyleFunction({ feature, drawType: getDrawType(toolMode), t }),
      maxPoints: displayMode === DisplayType.SOLAR_PANELS ? 4 : undefined,
    });

    const rightClickEndEventKey = map.on("pointerdown", (event) =>
      triggerFinishDrawing({
        event,
        drawInteraction,
        abortRef,
        onAbortFeature,
      }),
    );
    const contextMenuEventKey = map.on("contextmenu", preventContextMenu);

    drawInteraction.on("drawstart", (event) => {
      handleDrawStart(event, toolMode);
    });

    drawInteraction.on("drawend", (event) => {
      if (abortRef.current) {
        event.feature.set("doNotAdd", true);
      }

      abortRef.current = false;
    });

    map.addInteraction(drawInteraction);

    // Cleanup function
    return () => {
      map.removeInteraction(drawInteraction);
      map.un("pointerdown", rightClickEndEventKey);
      map.un("contextmenu", contextMenuEventKey);
    };
  }, [map, toolMode, onAbortFeature, displayMode, t]);

  return (
    <div ref={mapRef} className="w-full h-full">
      {tooltip && (
        <div
          className="bg-white rounded-full shadow-md p-2 text-xs text-center font-bold"
          style={{
            display: tooltip ? "block" : "none",
            position: "absolute",
            zIndex: 1000,
            left: tooltip.position[0] + 20,
            top: tooltip.position[1] - 10,
          }}
        >
          {tooltip?.text}
        </div>
      )}
    </div>
  );
};

export default memo(TiffViewer);
